import { ReactNode } from "react";
import ApiGet from "../../../../../api-controller-mod/api-serv/get-api/getApi";
import { API_ENDPOINTS } from "../../../../../context-controller-mod/context-serv/global-provider/api-shell/api-provider";
import ApiDelete from "../../../../../api-controller-mod/api-serv/delete-api/deleteApi";
import { BadgeVariant } from "../../../../../component-controller-mod/component-serv/badge-fs-cp/dynamic-badge-op/ts/statusBadge";
import ApiPost from "../../../../../api-controller-mod/api-serv/post-api/postApi";
import ApiPut from "../../../../../api-controller-mod/api-serv/put-api/putApi";

export type ApiListResponse<T> = {
  success: boolean;
  requestId: string;
  data: {
    items: T[];
    pagination: InboxPagination;
  };
};

export type ApiSingleResponse<T> = {
  success: boolean;
  requestId: string;
  data: T;
};

export type JobIndexFilters = {
  search?: string;
  department?: string | number; 
  level?: string | number;     
  status?: string;          
  jobType?: string | number; 
};

export type FieldErrors = {
  jobLevel?: string;
  jobType?: string;
  department?: string;
  status?: string;
  jobCode?: string; 
  jobTitle?: string; 
};

export const getJobIndexApi = (page = 1, size = 10, filters?: JobIndexFilters) => {
  const params: Record<string, any> = { page, size };

  if (filters?.search && String(filters.search).trim() !== "") {
    params.search = String(filters.search).trim();
  }

  if (filters?.department != null && String(filters.department) !== "" && String(filters.department) !== "all") {
    params.department = Number(filters.department);
  }

  if (filters?.level != null && String(filters.level) !== "" && String(filters.level) !== "all") {
    params.level = Number(filters.level);
  }

  if (filters?.jobType != null && String(filters.jobType) !== "" && String(filters.jobType) !== "all") {
    params.jobType = Number(filters.jobType); 
  }

  if (filters?.status && filters.status !== "all") {
    params.status = filters.status;
  }

  return ApiGet<ApiListResponse<JobIndexItem>>(API_ENDPOINTS.GET_JOB_INBOX, { params });
};

export type CareerApplicationItem = {
  id: number;
  applicationId: string;
  candidateName: string;
  jobCode: string;
  jobTitle: string;
  status: string;
  assignedRecruiterId?: string | null;
  isRead: boolean;
  appliedAt: string;
  createdAt: string;
  updatedAt: string;
};

export const columns = [
  'ID',
  'Position',
  'Job level',
  'Department',
  'Job type',
  'Publish date',
  'Status',
  'Action'
];

export type InboxRow = {
  id: string;
  ID: string;
  Position: string;
  "Job level": string;
  Department: string;
  "Job type": string;
  "Publish date": string;
  Status: string;
  Action?: ReactNode;
};

export type JobIndexItem = {
  id: number;
  jobId: string;
  jobCode: string;
  jobTitle: string;
  slug: string;
  departmentName: string | null;
  jobLevelName: string | null;
  jobType: string | null;
  hiring: number | null;
  location: string | null;
  applicationDeadline: string | null;
  status: string;
  publishedAt: string | null;
  closedAt: string | null;
  createdAt: string;
};

export const status = [
  { value: 'all', label: 'All' },
  { value: 'read', label: 'Read' },
  { value: 'new', label: 'New' },
];

export const TypeBussiness = [
  { value: 'all', label: 'All' },
  { value: 'business', label: 'Business' },
  { value: 'sale', label: 'Sale' },
];

export type InboxItem = {
  id?: string | number;
  contactId?: string | number;
  fullName?: string;
  name?: string;
  categoryName?: string;
  contactType?: string;
  email?: string;
  phone?: string;
  phoneNumber?: string;
  status?: string;
};

export type InboxPagination = {
  page: number;
  size: number;
  totalRecords: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
};

export type InboxApiResponse = {
  data?: {
    items?: InboxItem[];
    pagination?: InboxPagination;
  };
};

export type InboxAttachment = {
  id: string;
  filename: string;
  mimeType?: string;
  fileSize?: number;
  url?: string;
  downloadUrl?: string | null;
};

export type InboxDetail = {
  id: string;
  fullName?: string;
  email?: string;
  phone?: string;
  company?: string;
  subject?: string;
  message?: string;
  status?: string;
  categoryName?: string;
  createdAt?: string;
  updatedAt?: string;
  attachments?: InboxAttachment[];
};

export type InboxDetailApiResponse = {
  data?: InboxDetail;
};

export type CareerAttachment = {
  attachmentId: string;
  fileName: string;
  fileSize: number; // looks like KB? (backend says 17)
  mimeType?: string;
  url?: string; // if backend adds later
};

export type CareerPreview = {
  applicationId: string;
  candidateName: string;
  email: string;
  country?: string | null;
  phone?: string | null;
  jobCode: string;
  jobTitle: string;
  jobLevel?: string | null;
  departmentName?: string | null;
  appliedAt: string;
  status: "new" | "shortlisted" | "interviewed" | "rejected" | "reviewed";
  assignedRecruiterId?: string | null;
  attachments?: CareerAttachment[];
};

export const deleteJobItem = (jobUuid: string) =>
  ApiDelete(API_ENDPOINTS.DELETE_CREATED_JOB, {
    params: { jobUuid },
  });

export const getCareerSubmitPreviewApi = (applicationId: string) =>
  ApiGet<ApiSingleResponse<CareerPreview>>(API_ENDPOINTS.GET_CAREER_SUBMIT_PREVIEW, {
    params: { applicationId },
  });

export const getInboxDetailApi = (inboxUuid: string) =>
  ApiGet<InboxDetailApiResponse>(API_ENDPOINTS.GET_INBOX_DETAIL, {
    params: { inboxUuid },
  });

export const deleteInboxItem = (inboxId: string) =>
  ApiDelete(API_ENDPOINTS.DELETE_INBOX, {
    params: { inboxId },
  });

export const toBadgeVariant = (value?: string): BadgeVariant => {
  switch (value?.toLowerCase()) {
    case "draft":
      return "unpublished";
    case "in_review":
      return "draft";
    case "approved":
      return "published";
    case "scheduled":
      return "reviewed";
    case "rejected":
      return "rejected";
    default:
      return "draft";
  }
};

export type DepartmentItem = {
  id: number;
  departmentUuid: string;
  name: string;
  code: string;
  slug: string;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type JobLevelItem = {
  id: number;
  uuid: string;
  name: string;
  code: string;
  rank: number;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type JobLevelIndexResponse = {
  success: boolean;
  requestId: string;
  data: {
    success: boolean;
    data: {
      items: JobLevelItem[];
    };
  };
};

export type ApiItemsResponse<T> = {
  success: boolean;
  requestId: string;
  data: {
    items: T[];
  };
};

export const getJobLevelIndexApi = () =>
  ApiGet<JobLevelIndexResponse>(API_ENDPOINTS.GET_JOB_LEVEL);

export type DepartmentIndexResponse = ApiItemsResponse<DepartmentItem>;

export const getDepartmentIndexApi = () =>
  ApiGet<DepartmentIndexResponse>(API_ENDPOINTS.GET_JOB_DEPARTMENT);

export type JobDetailItem = {
  jobId: string;
  jobCode: string;
  jobTitle: string;
  slug: string;

  description: string | null;

  responsibilities: string | null;
  requirements: string | null;
  offer: string | null;
  qualification?: string | null; 

  departmentName: string | null;
  jobLevelName: string | null;
  jobType: string | null;

  location: string | null;
  expiryAt: string | null;

  status: string;
  publishedAt: string | null;
  closedAt: string | null;
  createdAt: string;
};


export const getJobDetailApi = (jobUuid: string) =>
  ApiGet<ApiSingleResponse<JobDetailItem>>(API_ENDPOINTS.GET_JOB_DETAIL, {
    params: { jobUuid },
  });

export type JobTypeItem = {
  id: number;
  uuid: string;
  name: string;
  code: string;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type JobTypeIndexResponse = {
  success: boolean;
  requestId: string;
  data: {
    items: JobTypeItem[];
  };
};

export const getJobTypeIndexApi = () =>
  ApiGet<JobTypeIndexResponse>(API_ENDPOINTS.GET_JOB_TYPE);

export type CreateJobBody = {
  jobTitle: string;
  jobCode?: string;
  departmentId: string;
  description: string;
  responsibilities: string; 
  requirements: string;
  qualification: string; 
  offer: string;
  jobLevelId: string; 
  jobTypeId: string;
  status: string;
};

export const createJobApi = (body: CreateJobBody) =>
  ApiPost(API_ENDPOINTS.CREATE_JOB, body);

export const CareerStatus = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_review', label: 'In review' },
  { value: 'approved', label: 'Approved' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'rejected', label: 'Rejected' },
];

export const updateJobApi = (jobUuid: string, body: CreateJobBody) =>
  ApiPut(API_ENDPOINTS.UPDATE_JOB, body, { params: { jobUuid } });

export const batchDeleteJobItems = (ids: string[]) =>
  ApiDelete(API_ENDPOINTS.BATCH_DELETE_CAREER_HIRE, {
    data: { ids },
  });